/**
 * Light / dark appearance. Default: follow the device (phone or computer setting). A choice made in
 * Settings applies to this device only (kept in localStorage), so a phone can follow the system
 * while a desktop is pinned to dark, for example.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';
const KEY = 'tc.theme';
const COLORS = { light: '#f4f5f7', dark: '#0b0d12' };

export function getTheme(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function effective(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice = getTheme()): void {
  const root = document.documentElement;
  if (choice === 'system') delete root.dataset.theme;
  else root.dataset.theme = choice;
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', COLORS[effective(choice)]);
  window.dispatchEvent(new Event('tc-theme')); // the phone app updates its status bar
}

export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* private mode: still applies for this visit */
  }
  applyTheme(choice);
}

// Keep the browser/phone status-bar colour in step when the device switches between light and dark.
if (typeof window !== 'undefined') {
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => applyTheme(getTheme()));
}
